import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export interface CenterNews {
  id: number;
  title: string;
  content: string;
  thumbnail_url: string | null;
  file_url: string | null;
  file_name: string | null;
  file_size: number | null;
  author: string;
  department: string;
  view_count: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

interface CenterNewsContextType {
  newsList: CenterNews[];
  loading: boolean;
  addNews: (news: Omit<CenterNews, 'id' | 'view_count' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateNews: (id: number, news: Partial<CenterNews>) => Promise<void>;
  deleteNews: (id: number) => Promise<void>;
  getNews: (id: number) => CenterNews | undefined;
  incrementViewCount: (id: number) => Promise<void>;
  refreshNews: () => Promise<void>;
}

const CenterNewsContext = createContext<CenterNewsContextType | undefined>(undefined);

export const CenterNewsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [newsList, setNewsList] = useState<CenterNews[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('center_news')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNewsList(data || []);
    } catch (error) {
      console.error('Error fetching center news:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const addNews = async (news: Omit<CenterNews, 'id' | 'view_count' | 'created_at' | 'updated_at'>) => {
    try {
      const { error } = await supabase
        .from('center_news')
        .insert({
          ...news,
          view_count: 0
        });

      if (error) throw error;
      await fetchNews();
    } catch (error) {
      console.error('Error adding center news:', error);
      throw error;
    }
  };

  const updateNews = async (id: number, news: Partial<CenterNews>) => {
    try {
      const { error } = await supabase
        .from('center_news')
        .update({ ...news, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      await fetchNews();
    } catch (error) {
      console.error('Error updating center news:', error);
      throw error;
    }
  };

  const deleteNews = async (id: number) => {
    try {
      const { error } = await supabase
        .from('center_news')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setNewsList(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      console.error('Error deleting center news:', error);
      throw error;
    }
  };

  const getNews = (id: number) => {
    return newsList.find(n => n.id === id);
  };

  const incrementViewCount = async (id: number) => {
    try {
      const news = newsList.find(n => n.id === id);
      if (!news) return;

      await supabase
        .from('center_news')
        .update({ view_count: news.view_count + 1 })
        .eq('id', id);

      setNewsList(prev =>
        prev.map(n => n.id === id ? { ...n, view_count: n.view_count + 1 } : n)
      );
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const refreshNews = async () => {
    await fetchNews();
  };

  return (
    <CenterNewsContext.Provider value={{
      newsList,
      loading,
      addNews,
      updateNews,
      deleteNews,
      getNews,
      incrementViewCount,
      refreshNews
    }}>
      {children}
    </CenterNewsContext.Provider>
  );
};

export const useCenterNews = () => {
  const context = useContext(CenterNewsContext);
  if (context === undefined) {
    throw new Error('useCenterNews must be used within a CenterNewsProvider');
  }
  return context;
};
